"use client"

import { Check, ChevronLeft, ChevronRight, Plus } from "lucide-react"
import { GET_SERVICES_WITH_CATEGORY, GET_SERVICE_CATEGORIES } from "@/lib/graphql"
import type { Service, ServiceCategory } from "@/lib/types/service.types"
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react"
import { observer } from "mobx-react-lite"

import type { CartServiceItem } from "@/lib/types/cart.types"
import { ServiceSelectionModal } from "./ServiceSelectionModal"
import { useQuery } from "@apollo/client/react"
import { useStore } from "@/app/components/common/store.provider"
import { Skeleton } from "@/app/components/ui/skeleton"
import { GroupGuestTabs, guestLabel } from "./GroupGuestTabs"

function toCartService(s: Service): CartServiceItem {
  return {
    id: Number(s.id),
    name: s.service_name,
    description: s.service_description,
    // GraphQL/TypeORM decimals often arrive as strings — coerce so cart math stays numeric
    price: Number(s.service_price) || 0,
    duration: Number(s.service_duration) || 0,
    targetAudience: s.target_audience,
  }
}

const FEATURED_OPTION: { id: string; name: string; slug: string } = {
  id: "featured",
  name: "Featured",
  slug: "featured",
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} mins`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (m === 0) return `${h} hr${h > 1 ? "s" : ""}`
  return `${h} hr${h > 1 ? "s" : ""}, ${m} mins`
}

function toPriceLabel(price: number): string {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(price)
}

function toAudienceLabel(audience: string): string {
  const map: Record<string, string> = {
    female: "Female only",
    male: "Male only",
    unisex: "Unisex",
    children: "Children",
  }
  return map[audience] ?? audience
}

type ServiceCardProps = {
  service: Service
  isSelected: boolean
  onClick: () => void
}

function ServiceCard({ service, isSelected, onClick }: ServiceCardProps) {
  const duration = formatDuration(service.service_duration)
  const audience = toAudienceLabel(service.target_audience)
  const details = [duration, audience].filter(Boolean).join(" · ")
  const [expandedDesc, setExpandedDesc] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const descRef = useRef<HTMLDivElement>(null)
  const hasDescription = Boolean(service.service_description?.trim())

  useEffect(() => {
    if (!hasDescription || expandedDesc) {
      setIsOverflowing(false)
      return
    }
    const el = descRef.current
    if (!el) return
    const check = () => setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
    check()
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null
    ro?.observe(el)
    return () => ro?.disconnect()
  }, [hasDescription, expandedDesc, service.service_description])

  const stopExpandToggle = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <div
      className={`flex items-start gap-4 cursor-pointer mr-4 hover:bg-muted rounded-lg border p-4 transition-colors ${
        isSelected ? "border-primary" : "border-border"
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) onClick()
      }}
      tabIndex={0}
    >
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-foreground">{service.service_name}</h3>
        <p className="text-sm text-muted-foreground">{details}</p>
        {hasDescription ? (
          <div className="mt-1">
            <div
              ref={descRef}
              className={`text-sm text-muted-foreground [&_a]:text-primary [&_a]:underline [&_p]:mb-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4 [&_strong]:font-semibold ${
                expandedDesc ? "" : "line-clamp-2"
              }`}
              dangerouslySetInnerHTML={{ __html: service.service_description ?? "" }}
            />
            {(isOverflowing || expandedDesc) && (
              <button
                type="button"
                className="mt-1 text-sm text-primary font-bold underline"
                onClick={(e) => {
                  stopExpandToggle(e)
                  setExpandedDesc((prev) => !prev)
                }}
                onKeyDown={stopExpandToggle}
              >
                {expandedDesc ? "Less" : "More"}
              </button>
            )}
          </div>
        ) : (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
            Professional hair care service.
          </p>
        )}
        <p className="mt-2 font-semibold text-foreground">
          {service.service_price > 0 ? `from ${toPriceLabel(service.service_price)}` : toPriceLabel(service.service_price)}
        </p>
      </div>
      <div
        className={`shrink-0 rounded-full p-2.5 transition-colors ${
          isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
        }`}
      >
        {isSelected ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
      </div>
    </div>
  )
}

export const Step1 = observer(function Step1() {
  const { appointmentStore } = useStore()
  const selectedServices = appointmentStore.activeServices
  const setSelectedServices = appointmentStore.setActiveServices.bind(appointmentStore)

  const categoryTabsRef = useRef<HTMLDivElement>(null)
  const sectionsRef = useRef<HTMLDivElement>(null)
  const [selectedSlug, setSelectedSlug] = useState<string>("featured")
  const [parentModalOpen, setParentModalOpen] = useState(false)
  const [parentForModal, setParentForModal] = useState<Service | null>(null)

  const CATALOG_PAGE_SIZE = 100

  const { data: categoriesData, loading: loadingCategories } = useQuery<{
    serviceCategories: { data: ServiceCategory[] }
  }>(GET_SERVICE_CATEGORIES, {
    variables: {
      pagination: { page: 1, limit: CATALOG_PAGE_SIZE },
      filters: { status: 1 },
    },
  })

  const { data: servicesData, loading: loadingServicesQuery } = useQuery<{
    services: { data: Service[] }
  }>(GET_SERVICES_WITH_CATEGORY, {
    variables: {
      pagination: { page: 1, limit: CATALOG_PAGE_SIZE },
      filters: {},
    },
  })

  const loadingServices = loadingCategories || loadingServicesQuery

  const allServices = useMemo(
    () => (servicesData?.services?.data ?? []) as Service[],
    [servicesData],
  )

  const childrenByParentId = useMemo(() => {
    const map = new Map<number, Service[]>()
    allServices.forEach((s) => {
      if (s.parentId != null) {
        const parentId = Number(s.parentId)
        if (!map.has(parentId)) map.set(parentId, [])
        map.get(parentId)!.push(s)
      }
    })
    for (const [parentId, children] of map) {
      map.set(
        parentId,
        [...children].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id,
        ),
      )
    }
    return map
  }, [allServices])

  const categories = useMemo(() => {
    const list = [...((categoriesData?.serviceCategories?.data ?? []) as ServiceCategory[])].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id,
    )
    return [FEATURED_OPTION, ...list] as Array<{
      id: string | number
      name: string
      slug: string
      sortOrder?: number
    }>
  }, [categoriesData])

  const servicesByCategory = useMemo(() => {
    const byCategory = new Map<string, Service[]>()
    // Option B: list every active service under its own category (and featured),
    // including children. parentId only drives the parent→children modal UX.
    const featured = allServices
      .filter((s) => s.featured && s.status !== 0)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id)
    byCategory.set("featured", featured)
    allServices.forEach((s) => {
      if (s.status === 0) return
      const cat = s.service_category
      const slug = cat?.slug ?? "other"
      if (!byCategory.has(slug)) byCategory.set(slug, [])
      byCategory.get(slug)!.push(s)
    })
    for (const [slug, services] of byCategory) {
      if (slug === "featured") continue
      byCategory.set(
        slug,
        [...services].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id,
        ),
      )
    }
    return byCategory
  }, [allServices])

  const scrollActiveTabIntoView = useCallback((slug: string) => {
    const container = categoryTabsRef.current
    const tab = container?.querySelector<HTMLElement>(
      `[data-category-tab="${CSS.escape(slug)}"]`,
    )
    if (!container || !tab) return

    const padding = 8
    const containerRect = container.getBoundingClientRect()
    const tabRect = tab.getBoundingClientRect()

    if (tabRect.left < containerRect.left + padding) {
      container.scrollBy({
        left: tabRect.left - containerRect.left - padding,
        behavior: "smooth",
      })
    } else if (tabRect.right > containerRect.right - padding) {
      container.scrollBy({
        left: tabRect.right - containerRect.right + padding,
        behavior: "smooth",
      })
    }
  }, [])

  const scrollToCategory = useCallback(
    (slug: string) => {
      setSelectedSlug(slug)
      const el = sectionsRef.current?.querySelector(`[data-category-slug="${slug}"]`)
      el?.scrollIntoView({ behavior: "smooth", block: "start" })
    },
    []
  )

  const handleServiceClick = useCallback(
    (service: Service) => {
      const children = childrenByParentId.get(service.id) ?? []
      if (children.length > 0) {
        setParentForModal(service)
        setParentModalOpen(true)
      } else {
        const item = toCartService(service)
        const exists = selectedServices.some((s) => s.id === service.id)
        const next = exists ? selectedServices.filter((s) => s.id !== service.id) : [...selectedServices, item]
        setSelectedServices(next)
      }
    },
    [childrenByParentId, selectedServices]
  )

  const isParentOrChildSelected = useCallback(
    (service: Service) => {
      if (selectedServices.some((s) => s.id === service.id)) return true
      const children = childrenByParentId.get(service.id) ?? []
      return children.some((c) => selectedServices.some((s) => s.id === c.id))
    },
    [selectedServices, childrenByParentId]
  )

  const handleModalSelect = useCallback(
    (child: Service) => {
      if (!parentForModal) return
      const childIds = new Set((childrenByParentId.get(parentForModal.id) ?? []).map((c) => c.id))
      const others = selectedServices.filter((s) => !childIds.has(s.id))
      const isAlreadySelected = selectedServices.some((s) => s.id === child.id)
      const next = isAlreadySelected ? others : [...others, toCartService(child)]
      setSelectedServices(next)
    },
    [parentForModal, childrenByParentId, selectedServices]
  )

  const handleModalRemove = useCallback(() => {
    if (!parentForModal) return
    const children = childrenByParentId.get(parentForModal.id) ?? []
    const childIds = new Set(children.map((c) => c.id))
    const next = selectedServices.filter((s) => !childIds.has(s.id))
    setSelectedServices(next)
    setParentModalOpen(false)
    setParentForModal(null)
  }, [parentForModal, childrenByParentId, selectedServices, setSelectedServices])

  useEffect(() => {
    const container = sectionsRef.current
    if (!container) return

    const scrollRoot =
      container.closest<HTMLElement>("[data-booking-step-scroll]") ?? null

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const slug = (entry.target as HTMLElement).dataset.categorySlug
          if (slug) setSelectedSlug(slug)
        }
      },
      {
        root: scrollRoot,
        rootMargin: "-80px 0px -55% 0px",
        threshold: 0,
      }
    )

    const sections = container.querySelectorAll("[data-category-slug]")
    sections.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [servicesByCategory, categories])

  useEffect(() => {
    if (loadingServices) return
    scrollActiveTabIntoView(selectedSlug)
  }, [selectedSlug, loadingServices, scrollActiveTabIntoView])

  const scrollTabs = (direction: "left" | "right") => {
    const el = categoryTabsRef.current
    if (!el) return
    el.scrollBy({ left: direction === "left" ? -200 : 200, behavior: "smooth" })
  }

  const parentChildren = parentForModal ? childrenByParentId.get(parentForModal.id) ?? [] : []
  const selectedChildId = parentForModal
    ? parentChildren.find((c) => selectedServices.some((s) => s.id === c.id))?.id ?? null
    : null

  const isGroup = appointmentStore.isGroupMode
  const activeMember = appointmentStore.activeMember
  const activeMemberIndex = activeMember
    ? appointmentStore.groupMembers.findIndex((m) => m.localId === activeMember.localId)
    : -1

  return (
    <div className="space-y-4">
      <h1 className="sr-only">Services</h1>
      {isGroup ? (
        <div className="space-y-3">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Choose services</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {activeMember && activeMemberIndex >= 0
                ? `Selecting for ${guestLabel(activeMember, activeMemberIndex)}. Switch guests below to pick theirs.`
                : "Add a guest to start picking services."}
            </p>
          </div>
          <GroupGuestTabs detail="services" />
        </div>
      ) : null}
      <div className="sticky top-0 z-20 -mx-3 border-b border-border/60 bg-background/95 px-3 pb-2 pt-1 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:-mx-6 md:px-6">
        <div className="flex items-center gap-2">
          <div
            ref={categoryTabsRef}
            className="flex flex-1 items-center gap-2 overflow-x-auto scroll-smooth scrollbar-hide py-1"
          >
            {loadingServices ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-20 shrink-0 rounded-full" />
              ))
            ) : (
              categories.map((cat) => {
                const slug = String(cat.slug ?? cat.id)
                const isSelected = selectedSlug === slug
                return (
                  <button
                    key={slug}
                    type="button"
                    data-category-tab={slug}
                    onClick={() => scrollToCategory(slug)}
                    className={`min-h-9 shrink-0 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                      isSelected
                        ? "bg-foreground text-background"
                        : "text-foreground hover:text-muted-foreground"
                    }`}
                  >
                    {cat.name}
                  </button>
                )
              })
            )}
          </div>
          <div className="hidden shrink-0 gap-2 sm:flex">
            <button
              type="button"
              onClick={() => scrollTabs("left")}
              className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollTabs("right")}
              className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div ref={sectionsRef} className="space-y-8 scroll-smooth">
        {loadingServices ? (
          <div className="space-y-8">
            {Array.from({ length: 2 }).map((_, sectionIdx) => (
              <section key={sectionIdx} className="space-y-4">
                <Skeleton className="h-7 w-32" />
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, cardIdx) => (
                    <div key={cardIdx} className="flex items-start gap-4 rounded-lg border p-4">
                      <div className="min-w-0 flex-1 space-y-2">
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="mt-2 h-5 w-24" />
                      </div>
                      <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          categories.map((cat) => {
            const slug = String(cat.slug ?? cat.id)
            const services = servicesByCategory.get(slug) ?? []
            // Keep Featured only when it has services; always show real categories.
            if (slug === "featured" && services.length === 0) return null

            return (
              <section
                key={slug}
                data-category-slug={slug}
                className="scroll-mt-20 space-y-4"
              >
                <h2 className="text-sm font-bold uppercase tracking-wide text-foreground">{cat.name}</h2>
                <div className="space-y-3">
                  {services.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No services in this category yet.</p>
                  ) : (
                    services.map((service) => (
                      <ServiceCard
                        key={service.id}
                        service={service}
                        isSelected={isParentOrChildSelected(service)}
                        onClick={() => handleServiceClick(service)}
                      />
                    ))
                  )}
                </div>
              </section>
            )
          })
        )}
      </div>

      {parentForModal && (
        <ServiceSelectionModal
          open={parentModalOpen}
          onOpenChange={(open) => {
            setParentModalOpen(open)
            if (!open) setParentForModal(null)
          }}
          parent={parentForModal}
          childServices={parentChildren}
          selectedChildId={selectedChildId}
          onSelect={handleModalSelect}
          onRemove={handleModalRemove}
        />
      )}
    </div>
  )
})
